import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { MainLayout } from './components/layout';
import {
  Login,
  ForgotPassword,
  ResetPassword,
  Dashboard,
  Licenses,
  LicenseDetail,
  Customers,
  CustomerDetail,
  Users,
  Partners,
  PartnerDetail,
  Owners,
  OwnerDetail,
  Products,
  Requests,
  Settings,
  Profile,
  NotFound,
} from './pages';
import { useAuthStore } from './store/authStore';
import type { ReactNode } from 'react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,
    },
  },
});

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
}

function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />

          {/* Protected routes */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Dashboard />} />

            <Route path="/profile" element={<Profile />} />

            <Route
              path="/licenses"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'OWNER', 'INVOICE_TEAM', 'CUSTOMER']}>
                  <Licenses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/licenses/:id"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'OWNER', 'INVOICE_TEAM', 'CUSTOMER']}>
                  <LicenseDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customers"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'OWNER', 'INVOICE_TEAM', 'PARTNER']}>
                  <Customers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers/:id"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'OWNER', 'INVOICE_TEAM', 'PARTNER']}>
                  <CustomerDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/owners"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <Owners />
                </ProtectedRoute>
              }
            />
            <Route
              path="/owners/:id"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <OwnerDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/partners"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'OWNER', 'INVOICE_TEAM']}>
                  <Partners />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partners/:id"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'OWNER', 'INVOICE_TEAM']}>
                  <PartnerDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/products"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'OWNER', 'INVOICE_TEAM']}>
                  <Products />
                </ProtectedRoute>
              }
            />

            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <Users />
                </ProtectedRoute>
              }
            />

            <Route
              path="/requests"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'OWNER', 'INVOICE_TEAM', 'PARTNER', 'CUSTOMER']}>
                  <Requests />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <Settings />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
    </QueryClientProvider>
  );
}
